import { isQuarterPoint, QUARTER_MESSAGE } from "@/lib/points";
import type { WorkdayLogConfig } from "@/lib/schema";
import type { WidgetValidationResult } from "@/widgets/types";
import { isWorkDuration } from "./duration";

export function validate(config: WorkdayLogConfig): WidgetValidationResult {
  const errors: Record<string, string> = {};

  if (config.label.trim().length === 0) {
    errors.label = "Etikett kan ikke være tom.";
  }
  if (!isWorkDuration(config.targetDuration)) {
    errors.targetDuration = "Normal arbeidsdag kreves, og må være over 0.";
  }
  if (!isWorkDuration(config.periodDuration)) {
    errors.periodDuration = "Lengde på en periode kreves, og må være over 0.";
  }

  // The hours half is floored to the same quarter grid these two are held to
  // (see ./scoring.ts), which is what makes every total this widget can
  // produce exact.
  if (!Number.isFinite(config.pointsAtTarget)) {
    errors.pointsAtTarget = "Poeng kreves.";
  } else if (config.pointsAtTarget <= 0) {
    errors.pointsAtTarget = "Poeng må være over 0.";
  } else if (!isQuarterPoint(config.pointsAtTarget)) {
    errors.pointsAtTarget = QUARTER_MESSAGE;
  }

  if (!Number.isFinite(config.pointsPerPeriod)) {
    errors.pointsPerPeriod = "Poeng per periode kreves.";
  } else if (config.pointsPerPeriod <= 0) {
    errors.pointsPerPeriod = "Poeng per periode må være over 0.";
  } else if (!isQuarterPoint(config.pointsPerPeriod)) {
    errors.pointsPerPeriod = QUARTER_MESSAGE;
  }

  // One goal per part (docs/adr/0010), each spelled out here rather than
  // through lib/points' shared Ukesmål rules: these carry their own labels,
  // and there is no goal-versus-max check to make anywhere here — both halves
  // are uncapped, so maxScoreWeek is Infinity and there is no ceiling to
  // measure a goal against (docs/adr/0003). The quarter grid still applies:
  // `weekGoal` below is their sum, and it is summed again across widgets.
  if (!Number.isFinite(config.hoursWeekGoal)) {
    errors.hoursWeekGoal = "Ukesmål for arbeidstid kreves.";
  } else if (config.hoursWeekGoal <= 0) {
    errors.hoursWeekGoal = "Ukesmål for arbeidstid må være over 0.";
  } else if (!isQuarterPoint(config.hoursWeekGoal)) {
    errors.hoursWeekGoal = QUARTER_MESSAGE;
  }

  if (!Number.isFinite(config.periodsWeekGoal)) {
    errors.periodsWeekGoal = "Ukesmål for produktivitet kreves.";
  } else if (config.periodsWeekGoal <= 0) {
    errors.periodsWeekGoal = "Ukesmål for produktivitet må være over 0.";
  } else if (!isQuarterPoint(config.periodsWeekGoal)) {
    errors.periodsWeekGoal = QUARTER_MESSAGE;
  }

  // Derived, never typed — a mismatch means something wrote the config
  // without going through the editor.
  if (
    Number.isFinite(config.hoursWeekGoal) &&
    Number.isFinite(config.periodsWeekGoal) &&
    config.weekGoal !== config.hoursWeekGoal + config.periodsWeekGoal
  ) {
    errors.weekGoal = "Samlet ukesmål må være summen av de to delmålene.";
  }

  if (Object.keys(errors).length === 0) return { ok: true };
  return { ok: false, errors };
}
